import { Drawer } from "@mui/material";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useColors from "../api/hooks/useColors";

const ResizableDrawer = forwardRef(
  (
    {
      children,
      initialWidth = 800,
      minWidth = 400,
      resizable = true,
      anchor = "right",
      PaperProps = {},
      ...drawerProps
    },
    ref
  ) => {
    const [drawerWidth, setDrawerWidth] = useState(initialWidth);
    const [drawerRef, setDrawerRef] = useState(null);
    const [handlePosition, setHandlePosition] = useState(null);
    const drawerZIndex = 1200;
    const [isHovering, setIsHovering] = useState(false);
    const [windowWidth, setWindowWidth] = useState(
      typeof window !== "undefined" ? window.innerWidth : 0
    );

    const animationFrameRef = useRef(null);
    const isResizing = useRef(false);
    const timeoutRef = useRef(null);
    const colors = useColors();

    const dynamicMaxWidth = Math.floor(windowWidth * 0.9);

    // Update handle position only if it really changed (with small epsilon)
    const updateHandlePosition = useCallback(() => {
      const handleWidth = 20;
      if (!drawerRef || !drawerProps.open || !resizable) {
        setHandlePosition((prev) => (prev === null ? prev : null));
        return;
      }

      const drawerRect = drawerRef.getBoundingClientRect();
      const handleOffset = 20;

      const next =
        anchor === "right"
          ? {
              left: drawerRect.left - handleOffset - handleWidth,
              top: drawerRect.top + drawerRect.height / 2,
            }
          : {
              left: drawerRect.right + handleOffset,
              top: drawerRect.top + drawerRect.height / 2,
            };

      setHandlePosition((prev) => {
        const eps = 0.5;
        if (
          prev &&
          Math.abs(prev.left - next.left) < eps &&
          Math.abs(prev.top - next.top) < eps
        ) {
          return prev; // skip update if not changed
        }
        return next;
      });
    }, [drawerRef, drawerProps.open, resizable, anchor]);

    // Mouse handlers for resize
    const handleMouseDown = (e) => {
      if (!resizable) return;

      // stop animation frame while resizing
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      isResizing.current = true;
      const startX = e.clientX;
      const startWidth = drawerWidth;

      const handleMouseMove = (e) => {
        if (!isResizing.current) return;

        let newWidth;
        if (anchor === "right") {
          newWidth = Math.max(
            minWidth,
            Math.min(dynamicMaxWidth, startWidth + (startX - e.clientX))
          );
        } else {
          newWidth = Math.max(
            minWidth,
            Math.min(dynamicMaxWidth, startWidth + (e.clientX - startX))
          );
        }

        // update only if width changed
        setDrawerWidth((prev) => (prev !== newWidth ? newWidth : prev));

        updateHandlePosition();
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "default";

        isResizing.current = false;

        // restart animation frame after resize
        setTimeout(() => {
          if (!animationFrameRef.current) {
            animationFrameRef.current = requestAnimationFrame(
              function animate() {
                if (!isResizing.current && drawerProps.open && resizable) {
                  updateHandlePosition();
                  animationFrameRef.current = requestAnimationFrame(animate);
                }
              }
            );
          }
        }, 10);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ew-resize";
      e.preventDefault(); // Prevent text selection
    };

    // Continuous update loop while drawer is open
    useEffect(() => {
      if (!drawerProps.open || !resizable || isResizing.current) {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        return;
      }

      function animate() {
        if (!isResizing.current && drawerProps.open && resizable) {
          updateHandlePosition();
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      }

      animate();

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      };
    }, [drawerProps.open, resizable, updateHandlePosition]);

    // Update window width on resize
    useEffect(() => {
      const handleWindowResize = () => {
        setWindowWidth((prev) => {
          const next = window.innerWidth;
          return prev !== next ? next : prev;
        });
      };

      window.addEventListener("resize", handleWindowResize);
      return () => {
        window.removeEventListener("resize", handleWindowResize);
      };
    }, []);

    // Update handle position on window resize/scroll
    useEffect(() => {
      if (!drawerProps.open || !resizable) return;

      const handleEvent = () => {
        if (!isResizing.current) {
          updateHandlePosition();
        }
      };

      window.addEventListener("resize", handleEvent);
      window.addEventListener("scroll", handleEvent, { passive: true });

      return () => {
        window.removeEventListener("resize", handleEvent);
        window.removeEventListener("scroll", handleEvent);
      };
    }, [drawerProps.open, resizable, updateHandlePosition]);

    // Cleanup all timeouts on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    // Portal-based handle
    const renderResizeHandle = () => {
      if (!resizable || !handlePosition || !drawerProps.open) return null;

      const isActive = isResizing.current || isHovering;

      const handleElement = (
        <div
          style={{
            position: "fixed",
            top: handlePosition.top,
            left: handlePosition.left,
            transform: "translateY(-50%)",
            width: "20px",
            height: "60px",
            cursor: "ew-resize",
            zIndex: drawerZIndex,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: isActive
              ? colors.white_32percent
              : colors.white_20percent,
            backdropFilter: "blur(10px)",
            border: "1px solid " + colors.white_20percent,
            borderRadius: "10px",
            transition: isResizing.current ? "none" : "all 0.2s ease",
            pointerEvents: "auto",
          }}
          onMouseDown={handleMouseDown}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "3px",
              opacity: 0.8,
            }}
          >
            {[...Array(2)].map((_, i) => (
              <div
                key={i}
                style={{
                  width: "2px",
                  height: "16px",
                  borderRadius: "1px",
                  backgroundColor: colors.common_white,
                  transition: isResizing.current ? "none" : "all 0.2s ease",
                }}
              />
            ))}
          </div>
        </div>
      );

      return createPortal(handleElement, document.body);
    };

    // Merge PaperProps with width and ref
    const mergedPaperProps = {
      ...PaperProps,
      ref: (element) => {
        if (element) {
          setDrawerRef((prev) => (prev === element ? prev : element));

          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => {
            updateHandlePosition();
          }, 0);
        }
        if (PaperProps.ref) {
          if (typeof PaperProps.ref === "function") {
            PaperProps.ref(element);
          } else {
            PaperProps.ref.current = element;
          }
        }
      },
      style: {
        width: `${drawerWidth}px`,
        transition: isResizing.current ? "none" : undefined,
        ...PaperProps.style,
      },
    };

    return (
      <>
        <Drawer
          ref={ref}
          anchor={anchor}
          PaperProps={mergedPaperProps}
          {...drawerProps}
        >
          {children}
        </Drawer>
        {renderResizeHandle()}
      </>
    );
  }
);

ResizableDrawer.displayName = "ResizableDrawer";

export default ResizableDrawer;
